import React, { useCallback, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { SipPhoneContext } from '../../../../components/SipPhoneProvider';
import RoundIconBackground from '../RoundIconBackground/RoundIconBackground';
import { CALL_STATUS_ACTIVE } from '../../../../components/SipPhoneProvider/constants/statuses';
import { setPhoneFieldPos, setPhoneFieldValue } from '../../store/actions';
import {
  numPadVisibleSelector,
  phoneFieldPosSelector,
  phoneFieldValueSelector,
} from '../../store/selectors';
import usePhoneFieldCallHandler from '../../hooks/usePhoneFieldCallHandler';
import {
  dispatcherDispexValue,
  phoneFieldDataAttrFocus,
} from '../../constants/common';
import styles from './NumPad.module.scss';
import useCallHandler from '../../hooks/useCallHandler';

const NumPad = () => {
  const { call, sendDTMF } = useContext(SipPhoneContext);

  const dispatch = useDispatch();

  const numPadVisible = useSelector(numPadVisibleSelector);
  const phoneFieldValue = useSelector(phoneFieldValueSelector);
  const phoneFieldPos = useSelector(phoneFieldPosSelector);
  const dispatcherUser = useSelector(
    (state) => state.authentication.userInfo.Dispatcher
  );

  const isDispatcherDispex = dispatcherUser === dispatcherDispexValue;

  const addChar = useCallback(
    (char) => {
      if (call.status === CALL_STATUS_ACTIVE) {
        sendDTMF(char);
      } else {
        const newValue =
          phoneFieldValue.slice(0, phoneFieldPos) +
          char +
          phoneFieldValue.slice(phoneFieldPos);
        dispatch(setPhoneFieldValue(newValue));
        dispatch(setPhoneFieldPos(phoneFieldPos + 1));
      }
    },
    [call.status, sendDTMF, phoneFieldValue, phoneFieldPos, dispatch]
  );

  const removeChar = useCallback(() => {
    if (phoneFieldPos === 0) return;

    const newValue =
      phoneFieldValue.slice(0, phoneFieldPos - 1) +
      phoneFieldValue.slice(phoneFieldPos);
    dispatch(setPhoneFieldValue(newValue));
    dispatch(setPhoneFieldPos(phoneFieldPos - 1));
  }, [phoneFieldPos, phoneFieldValue, dispatch]);

  const handleToneBtnClick = (e) => {
    addChar(e.target.innerText);
  };

  const { handleCall } = usePhoneFieldCallHandler();

  const { handleClickCall } = useCallHandler();

  useEffect(() => {
    const handleKeyDown = (e) => {
      const activeElement = document.activeElement;
      if (activeElement.dataset.focus !== phoneFieldDataAttrFocus) {
        if (!isNaN(e.key) || e.key === '*' || e.key === '#' || e.key === '+') {
          addChar(e.key);
        } else if (e.key === 'Backspace') {
          removeChar();
        }
      }

      if (e.key === 'Enter') {
        if (!isDispatcherDispex) {
          handleCall();
          return;
        }

        handleClickCall();
      }
    };

    if (numPadVisible) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [numPadVisible, addChar, removeChar, handleCall]);

  return (
    <div className={styles.root}>
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, '*', 0, '#'].map((num) => (
        <RoundIconBackground
          key={num}
          className={styles.item}
          onClick={handleToneBtnClick}
        >
          <span
            className={cn(styles.content, {
              [styles.text]: typeof num === 'string',
              [styles.num]: typeof num === 'number',
              [styles.mt]: num === '*',
            })}
          >
            {num}
          </span>
        </RoundIconBackground>
      ))}

      <RoundIconBackground
        className={styles.item}
        onClick={handleToneBtnClick}
        style={{
          gridColumn: '1 / span 2',
          gridRow: '5/6',
        }}
      >
        <span className={cn(styles.content, styles.text, styles['mb-8'])}>
          +
        </span>
      </RoundIconBackground>
      <RoundIconBackground
        icon="step-backward"
        style={{
          gridColumn: '2 / span 2',
          gridRow: '5/6',
        }}
        className={cn(styles.num)}
        onClick={removeChar}
      />
    </div>
  );
};

export default NumPad;
